import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import Logo from "./Logo";
import { useSkin } from "@/lib/skin";

const navItems = [
  { to: "/how-it-works", label: "How It Works", id: "nav-how" },
  { to: "/sell-your-car", label: "Sell Your Car", id: "nav-sell" },
  { to: "/service-areas", label: "Service Areas", id: "nav-areas" },
  { to: "/about", label: "About", id: "nav-about" },
  { to: "/faq", label: "FAQ", id: "nav-faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = useSkin() === "dark";
  const { pathname } = useLocation();
  const navyTop = !dark && pathname === "/" && !scrolled;
  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${dark ? "header-dark" : ""} ${navyTop ? "header-navy bg-brand-navy border-b border-transparent" : ""} ${
        dark
          ? scrolled ? "bg-[#0A0A0B]/90 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
          : navyTop
          ? ""
          : scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)]"
          : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Logo testId="header-logo" />

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.id}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  isActive
                    ? "text-brand-blue bg-blue-50"
                    : "text-slate-700 hover:text-brand-blue hover:bg-slate-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="tel:+16503396211"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-brand-blue"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4" />
            (650) 339-6211
          </a>
          <Link to="/sell-your-car" data-testid="header-cta">
            <Button className="bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-full h-11 px-6 shadow-lg shadow-orange-500/30">
              Request Offer
            </Button>
          </Link>
        </div>

        {/* Mobile menu */}
        <div className="lg:hidden flex items-center gap-2">
          <Link to="/sell-your-car" data-testid="header-cta-mobile">
            <Button className="bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-full h-10 px-4 text-sm">
              Request Offer
            </Button>
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] sm:w-[420px] p-0">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
                <SheetTitle className="text-left">
                  <Logo testId="mobile-logo" />
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col px-3 py-4" data-testid="mobile-nav">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${item.id}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                        isActive
                          ? "text-brand-blue bg-blue-50"
                          : "text-slate-800 hover:bg-slate-50"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <NavLink
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-contact"
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                      isActive ? "text-brand-blue bg-blue-50" : "text-slate-800 hover:bg-slate-50"
                    }`
                  }
                >
                  Contact
                </NavLink>
              </nav>
              <div className="px-6 pb-8 mt-auto space-y-3">
                <a
                  href="tel:+16503396211"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 text-slate-800 font-semibold"
                  data-testid="mobile-phone"
                >
                  <Phone className="w-4 h-4 text-brand-blue" />
                  (650) 339-6211
                </a>
                <Link to="/sell-your-car" onClick={() => setOpen(false)}>
                  <Button className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold rounded-xl">
                    Request my offer
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
